import type { DocumentReference } from 'fhir/r4';
import moment from 'moment';
import { v4 as uuid } from 'uuid';

import * as FHIRUtils from '../fhirutils';

export function build(
  patientId: string,
  noteText: string,
  practitionerId: string,
  noteCategory: string, //http://hl7.org/fhir/valueset-c80-doc-typecodes.html
  noteCategoryDisplay: string,
  noteTypeCode: string, //http://hl7.org/fhir/valueset-c80-doc-typecodes.html
  noteTypeDisplay: string,
  noteTypeSystem: string,
  taskNote: boolean,
  taskId: string,
  updatedBy: string,
  questionnaireResponseId: string | null | undefined,
  /*
   * `string[]`, not the `[] | undefined` this once carried: the empty-tuple type admits
   * only a literally empty array, so `patient/notes/new.tsx` could not pass referral
   * ids. The body pushes each element as a `referral-id` identifier value.
   */
  referalId: string[] | undefined = undefined
): DocumentReference {
  const noteId = uuid();

  const note: DocumentReference = {
    resourceType: 'DocumentReference',
    id: noteId,
    status: 'current',
    category: [
      {
        coding: [
          {
            system: 'http://loinc.org',
            code: noteCategory,
            display: noteCategoryDisplay,
          },
        ],
      },
    ],
    type: {
      coding: [
        {
          system: noteTypeSystem,
          code: noteTypeCode,
          display: noteTypeDisplay,
        },
      ],
    },
    subject: {
      reference: 'Patient/' + patientId,
    },
    date: moment().format(),
    author: [
      {
        reference: 'Practitioner/' + practitionerId,
        display: updatedBy,
      },
    ],
    description: noteText,
    content: [
      {
        attachment: {},
        format: {
          system: 'http://ihe.net/fhir/ValueSet/IHE.FormatCode.codesystem',
          code: 'urn:ihe:pcc:cm:2008',
          display: 'Care Management (CM)',
        },
      },
    ],
  };

  if (taskNote) {
    note.identifier = [
      {
        system: 'https://projectwell.io/fhir/identifiers/note-type',
        value: 'task',
      },
    ];
    note.context = {
      related: [
        {
          reference: 'Task/' + taskId,
        },
      ],
    };
  } else {
    note.identifier = [
      {
        system: 'https://projectwell.io/fhir/identifiers/note-type',
        value: 'note',
      },
    ];
  }
  if (questionnaireResponseId) {
    note.identifier.push({
      system: 'https://projectwell.io/fhir/identifiers/questionnaireResponse',
      value: questionnaireResponseId,
    });
  }
  if (referalId && referalId?.length > 0) {
    for (let i = 0; i < referalId.length; i++) {
      note.identifier.push({
        system: 'https://projectwell.io/fhir/identifiers/referral-id',
        value: referalId[i],
      });
    }
  }
  return FHIRUtils.addUpdatedBy(note, updatedBy);
}

export function buildComment(
  patientId: string,
  noteId: string,
  commentText: string,
  practitionerId: string,
  noteCategory: string,
  noteCategoryDisplay: string,
  updatedBy: string,
  referalId: string[] | undefined = undefined
): DocumentReference {
  const commentNote: DocumentReference = build(
    patientId,
    commentText,
    practitionerId,
    noteCategory,
    noteCategoryDisplay,
    '48767-8',
    'Annotation comment Narrative',
    'http://loinc.org',
    false,
    '',
    updatedBy,
    null,
    referalId
  );
  commentNote.relatesTo = [
    {
      code: 'appends',
      target: {
        reference: 'DocumentReference/' + noteId,
      },
    },
  ];

  return FHIRUtils.addUpdatedBy(commentNote, updatedBy);
}

/**
 * Reads a file as a base64 data URL.
 *
 * Resolves with `FileReader.result` verbatim — the full union, not `string`, since
 * this function never checks the shape. Callers narrow.
 */
export const fileToBase64 = (file: Blob): Promise<FileReader['result']> => {
  return new Promise<FileReader['result']>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result); // base64 string
    // Rejects with the FileReader's `ProgressEvent`, not an `Error`. Preserved: the
    // two callers pass the rejection to `ErrorResponse.getMessage`, which has a
    // branch for a non-Error value.
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file);
  });
};
